import React, { useEffect } from 'react';
import CartStep1 from '../step-1/cart-step-1';
import EmptyCart from '../empty-cart/empty-cart';
import { billingAddressActions, rootActions, useAppDispatch, useAppSelector } from '../../data/store';
import { checkIsVoiceRegenFreeSubscription, removeQueryStringsOnLoad } from '../../domain/shopping-cart-provider';
import { CartSteps } from '../../enums/cart-steps';
import CartStep2 from '../step-2/cart-step-2';
import CartStep3 from '../step-3/cart-step-3';
import {
    getLocalData,
    useSaveAddressesToStorage,
    useUpdateAddressFromResponse
} from '../../domain/shopping-cart-address-provider';
import CartDesktopHeader from './cart-desktop-header';
import { getUniqueRemovedCartItems } from '../../domain/undo-cart-item-provider';
import { restoreCartDataFromStorage } from '../../data/cart-storage-provider';
import { getData } from '../../data/data-service';
import { tiktokAddToCart } from '../../../../../domain/marketing/tiktok-provider';
import { facebookAddToCart } from '../../../../../domain/facebook/facebook-marketing-provider';
import { initFacebookForLogin } from '../../../../../domain/facebook/facebook-login-provider';
import { initGoogleForLogin } from '../../../../../domain/google/google-login-provider';
import { UPDATE_MINI_CART_COUNT_EVENT } from '../../../../site-header/top-minicart/ui/main';
import CartStep3WithoutLeftSide from '../step-3/cart-step-3-without-left-side';

/**
 * The shopping cart page.
 */
const CartPage = () => {

    const dispatch = useAppDispatch();

    const cartData = useAppSelector(store => store.root.cartData);
    const cartStep = useAppSelector(store => store.root.cartStep);
    const isEmpty = !cartData || !cartData.cartItems || cartData.cartItems.length <= 0;

    const updateCount = useAppSelector(store => store.billingAddress.updateCount);

    const isInAppPurchase = useAppSelector(store => store.root.isInAppPurchase);
    const showNoSessionPanel = useAppSelector(store => store.root.showNoSessionPanel);

    useEffect(() => {
        initFacebookForLogin();
        initGoogleForLogin(false, undefined, true, getLocalData());

        // update top min-cart products count -----------------
        document.dispatchEvent(new CustomEvent(UPDATE_MINI_CART_COUNT_EVENT));
    }, []);

    // update address controls on page load and after log in.
    useUpdateAddressFromResponse();

    // each time address controls change ---> save updated cart data to the storage, for example, after user input
    useSaveAddressesToStorage();

    /**
     * page load
     */
    useEffect(() => {

        if(isInAppPurchase && cartData.inAppPurchaseResult?.statusCode !== 0){
            // show "no session" panel when there is an in-app
            dispatch(
                rootActions.main({
                    showNoSessionPanel: true,
                })
            );
            return;
        }

        // remove 'productid' query string if exists;
        // the functionality is handled on server-side;
        // for in-app, and for cart reconstruction, remove all the relevant query strings
        removeQueryStringsOnLoad();

        // update the address controls
        // from cart data for the first time on page load
        dispatch(
            billingAddressActions.main({
                updateCount: updateCount + 1,
            })
        );

        // init 'undo cart items' - https://wavesaudio.atlassian.net/browse/DSWEB-1017
        dispatch(
            rootActions.main({
                removedItems: getUniqueRemovedCartItems(cartData),
            })
        );

        // try to get cart data from session storage
        const storageData = restoreCartDataFromStorage();
        if(!isEmpty && storageData){
            // if storage data exists, there may be some information like country / state
            // that should be passed to the server to receive tax etc.
            // -----> we need to ask for cart data from th server again.
            (async () => {
                const response = await getData(
                    getLocalData()
                );

                if(!response || response.statusCode !== 200 || !response.data.isValid || !response.data.relatedData) return;

                dispatch(
                    rootActions.setCartData(response.data.relatedData)
                );
            })();
        }

        // Send 'add to cart' marketing scripts.
        tiktokAddToCart(cartData);
        facebookAddToCart(cartData);
    },
    // the dependencies list should not contain updateCount, otherwise we have an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]);

    const isVoiceRegenFreeSubscriptionPurchase = checkIsVoiceRegenFreeSubscription(cartData);

    return (
        <div className="bg-gray-100 min-h-screen pt-2 pb-10 md:py-8 w-full">
            <div className="waves-box-lg mx-auto px-5 md:px-12">
                {
                    showNoSessionPanel && (
                        <div className="mx-auto my-4 waves-box-sm">
                            <div className="border bg-white rounded px-6 py-4 mx-4"><p>No Session</p></div>
                        </div>
                    )
                }

                {
                    !showNoSessionPanel && (
                        <>
                            <CartDesktopHeader />

                            { isEmpty && <EmptyCart/> }

                            {
                                !isEmpty &&
                                <>
                                    { cartStep === CartSteps.First && <CartStep1 /> }

                                    { cartStep === CartSteps.Second && <CartStep2 /> }

                                    {
                                        cartStep === CartSteps.Third &&
                                        <>
                                            {
                                                !isVoiceRegenFreeSubscriptionPurchase &&
                                                <CartStep3 />
                                            }

                                            {
                                                isVoiceRegenFreeSubscriptionPurchase &&
                                                <CartStep3WithoutLeftSide />
                                            }
                                        </>
                                    }
                                </>
                            }
                        </>
                    )
                }

            </div>
        </div>
    )
};

export default CartPage;